import React, { useState, useEffect } from 'react';
import adminImageStyleService, { AdminImageStyle, ImageStyleCreateRequest, ImageStyleUpdateRequest, AdminPlatformType, ImageStyleCategory } from '../../services/api/adminImageStyleService';
import ImageUpload from './ImageUpload';
import {
  TaskyAlert,
  TaskyButton,
  TaskyCheckbox,
  TaskyFormGroup,
  TaskyInput,
  TaskyLabel,
  TaskySelect,
  TaskySwitch,
  TaskyTextarea,
} from '../tasky';

// Platform configuration for display
const PLATFORM_OPTIONS: { value: AdminPlatformType; label: string }[] = [
  { value: 'instagram', label: 'Instagram' },
  { value: 'facebook', label: 'Facebook' },
  { value: 'youtube', label: 'YouTube' },
  { value: 'x', label: 'X (Twitter)' },
  { value: 'tiktok', label: 'TikTok' },
  { value: 'line', label: 'LINE' },
  { value: 'yahoo_banner', label: 'Yahoo! 広告' },
  { value: 'google_banner', label: 'Google 広告' },
  { value: 'smartnews', label: 'SmartNews' },
  { value: 'website', label: 'Web サイト' },
  { value: 'mailmag', label: 'メルマガ' },
];

interface ImageStyleFormProps {
  imageStyle?: AdminImageStyle | null;
  existingCategories: ImageStyleCategory[];
  onSubmit: (data: ImageStyleCreateRequest | ImageStyleUpdateRequest) => void;
  onCancel: () => void;
  isLoading?: boolean;
}

const ImageStyleForm: React.FC<ImageStyleFormProps> = ({
  imageStyle,
  existingCategories,
  onSubmit,
  onCancel,
  isLoading = false
}) => {
  const [formData, setFormData] = useState({
    category: '',
    category_id: undefined as number | undefined,
    subcategory_id: undefined as number | undefined,
    platforms: [] as AdminPlatformType[],
    name: '',
    prompt_template: '',
    description: '',
    tags_text: '',
    reference_image_url: '',
    thumbnail_url: '',
    sort_order: 0,
    is_active: true
  });

  const [errors, setErrors] = useState<Record<string, string>>({});

  useEffect(() => {
    if (imageStyle) {
      setFormData({
        category: imageStyle.category,
        category_id: imageStyle.category_id,
        subcategory_id: imageStyle.subcategory_id ?? undefined,
        platforms: imageStyle.platforms || [],
        name: imageStyle.name,
        prompt_template: imageStyle.prompt_template ?? '',
        description: imageStyle.description || '',
        tags_text: (imageStyle.tags || []).join(', '),
        reference_image_url: imageStyle.reference_image_url || '',
        thumbnail_url: imageStyle.thumbnail_url || '',
        sort_order: imageStyle.sort_order,
        is_active: imageStyle.is_active
      });
    } else {
      setFormData({
        category: '',
        category_id: existingCategories.length > 0 ? existingCategories[0].id : undefined,
        subcategory_id: undefined,
        platforms: [],
        name: '',
        prompt_template: '',
        description: '',
        tags_text: '',
        reference_image_url: '',
        thumbnail_url: '',
        sort_order: 0,
        is_active: true
      });
    }
  }, [imageStyle, existingCategories]);

  // 一覧 API は prompt_template を返さない（本番 3,900 件 × 20MB で 500 になるため）。
  // 編集時は 1 件取得で全文を補い、取得が終わるまでは保存させない（空文字で上書きしないため）。
  const [isLoadingTemplate, setIsLoadingTemplate] = useState(false);
  const [templateError, setTemplateError] = useState<string | null>(null);
  const needsTemplateFetch = !!imageStyle && imageStyle.prompt_template === undefined;
  const styleId = imageStyle?.id;

  useEffect(() => {
    if (!needsTemplateFetch || !styleId) {
      setIsLoadingTemplate(false);
      setTemplateError(null);
      return undefined;
    }

    let cancelled = false;
    setIsLoadingTemplate(true);
    setTemplateError(null);

    adminImageStyleService
      .getStyle(styleId)
      .then((full) => {
        if (cancelled) return;
        setFormData((prev) => ({ ...prev, prompt_template: full.prompt_template ?? '' }));
      })
      .catch(() => {
        if (cancelled) return;
        setTemplateError('プロンプトテンプレートを取得できませんでした。モーダルを開き直してください。');
      })
      .finally(() => {
        if (!cancelled) setIsLoadingTemplate(false);
      });

    return () => {
      cancelled = true;
    };
  }, [needsTemplateFetch, styleId]);

  const validateForm = () => {
    const newErrors: Record<string, string> = {};

    if (!formData.category_id && !formData.category.trim()) {
      newErrors.category = 'カテゴリは必須です';
    } else if (formData.category && formData.category.length > 255) {
      newErrors.category = 'カテゴリは255文字以内で入力してください';
    }

    if (!formData.name.trim()) {
      newErrors.name = 'スタイル名は必須です';
    } else if (formData.name.length > 255) {
      newErrors.name = 'スタイル名は255文字以内で入力してください';
    }

    if (!formData.prompt_template.trim()) {
      newErrors.prompt_template = 'プロンプトテンプレートは必須です';
    }

    if (formData.description && formData.description.length > 1000) {
      newErrors.description = '説明は1000文字以内で入力してください';
    }

    if (formData.reference_image_url && formData.reference_image_url.length > 1000) {
      newErrors.reference_image_url = '参照画像URLは1000文字以内で入力してください';
    }

    if (formData.thumbnail_url && formData.thumbnail_url.length > 1000) {
      newErrors.thumbnail_url = 'サムネイルURLは1000文字以内で入力してください';
    }

    if (formData.sort_order < 0) {
      newErrors.sort_order = '並び順は0以上の数値を入力してください';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e: React.FormEvent) => {
    if (isLoadingTemplate || templateError) {
      e.preventDefault();
      return;
    }
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    const submitData: ImageStyleCreateRequest | ImageStyleUpdateRequest = {
      category_id: formData.category_id,
      // null を明示送信して、編集時にタグを外せるようにする
      subcategory_id: formData.subcategory_id ?? null,
      category: formData.category.trim() || undefined,
      platforms: formData.platforms.length > 0 ? formData.platforms : undefined,
      name: formData.name.trim(),
      prompt_template: formData.prompt_template.trim(),
      description: formData.description.trim() || undefined,
      tags: formData.tags_text
        .split(/[、,\n]/)
        .map(tag => tag.trim())
        .filter(Boolean),
      reference_image_url: formData.reference_image_url.trim() || undefined,
      thumbnail_url: formData.thumbnail_url.trim() || undefined,
      sort_order: formData.sort_order,
      is_active: formData.is_active
    };

    onSubmit(submitData);
  };

  const handleInputChange = (field: string, value: any) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));

    // Clear error
    if (errors[field]) {
      setErrors(prev => ({
        ...prev,
        [field]: ''
      }));
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-5">
      <TaskyFormGroup label="カテゴリ" required htmlFor="category_id" error={errors.category || undefined}>
        {existingCategories.length > 0 ? (
          <TaskySelect
            id="category_id"
            hasError={Boolean(errors.category)}
            value={formData.category_id || ''}
            onChange={(e) => {
              const categoryId = e.target.value ? parseInt(e.target.value) : undefined;
              handleInputChange('category_id', categoryId);
              // 小カテゴリは大カテゴリに従属するため、カテゴリ変更時にリセット
              handleInputChange('subcategory_id', undefined);
              // Also set category slug for backward compatibility
              const selectedCategory = existingCategories.find(c => c.id === categoryId);
              if (selectedCategory) {
                handleInputChange('category', selectedCategory.slug);
              }
            }}
            disabled={isLoading}
          >
            <option value="">カテゴリを選択してください</option>
            {existingCategories.map(cat => (
              <option key={cat.id} value={cat.id}>
                {cat.name} {cat.styles_count !== undefined && `(${cat.styles_count})`}
              </option>
            ))}
          </TaskySelect>
        ) : (
          <TaskyAlert tone="warning">
            カテゴリが登録されていません。先にカテゴリを作成してください。
          </TaskyAlert>
        )}
      </TaskyFormGroup>

      {(() => {
        const selectedCategory = existingCategories.find(c => c.id === formData.category_id);
        const subcategories = selectedCategory?.subcategories ?? [];
        if (subcategories.length === 0) return null;
        return (
          <TaskyFormGroup
            label="小カテゴリ（任意）"
            htmlFor="subcategory_id"
            helper="バナー収集元（x-ad中分類）の粒度でタグ付けされます。表示は大カテゴリ単位のままです。"
          >
            <TaskySelect
              id="subcategory_id"
              value={formData.subcategory_id || ''}
              onChange={(e) => {
                handleInputChange('subcategory_id', e.target.value ? parseInt(e.target.value) : undefined);
              }}
              disabled={isLoading}
            >
              <option value="">指定なし</option>
              {subcategories.map(sub => (
                <option key={sub.id} value={sub.id}>
                  {sub.name}
                </option>
              ))}
            </TaskySelect>
          </TaskyFormGroup>
        );
      })()}

      <div className="space-y-1.5">
        <TaskyLabel className="mb-0">対象プラットフォーム</TaskyLabel>
        <div className="grid grid-cols-2 gap-x-3 gap-y-2 rounded-tasky-md border border-warm-border bg-warm-bg p-4 sm:grid-cols-3">
          {PLATFORM_OPTIONS.map(option => (
            <TaskyCheckbox
              key={option.value}
              label={option.label}
              checked={formData.platforms.includes(option.value)}
              onChange={(e) => {
                const newPlatforms = e.target.checked
                  ? [...formData.platforms, option.value]
                  : formData.platforms.filter(p => p !== option.value);
                handleInputChange('platforms', newPlatforms);
              }}
              disabled={isLoading}
            />
          ))}
        </div>
        <p className="text-xs text-gray-500">
          このスタイルが適用されるプラットフォームを選択してください（複数選択可）
        </p>
      </div>

      <TaskyFormGroup label="スタイル名" required htmlFor="name" error={errors.name || undefined}>
        <TaskyInput
          type="text"
          id="name"
          hasError={Boolean(errors.name)}
          value={formData.name}
          onChange={(e) => handleInputChange('name', e.target.value)}
          placeholder="スタイル名を入力してください"
          disabled={isLoading}
          maxLength={255}
        />
      </TaskyFormGroup>

      <TaskyFormGroup
        label="プロンプトテンプレート"
        required
        htmlFor="prompt_template"
        error={errors.prompt_template || undefined}
        helper="画像生成AIに送信されるプロンプトのテンプレートです"
      >
        <TaskyTextarea
          id="prompt_template"
          hasError={Boolean(errors.prompt_template)}
          value={formData.prompt_template}
          onChange={(e) => handleInputChange('prompt_template', e.target.value)}
          placeholder={isLoadingTemplate ? 'プロンプトテンプレートを読み込み中...' : '画像生成に使用するプロンプトテンプレートを入力してください'}
          disabled={isLoading || isLoadingTemplate}
          rows={6}
        />
        {templateError && (
          <TaskyAlert tone="error" className="mt-2">
            {templateError}
          </TaskyAlert>
        )}
      </TaskyFormGroup>

      <TaskyFormGroup
        label="説明"
        htmlFor="description"
        error={errors.description || undefined}
        helper={`${formData.description.length}/1000文字`}
      >
        <TaskyTextarea
          id="description"
          hasError={Boolean(errors.description)}
          value={formData.description}
          onChange={(e) => handleInputChange('description', e.target.value)}
          placeholder="スタイルの説明を入力してください（任意）"
          disabled={isLoading}
          rows={3}
          maxLength={1000}
        />
      </TaskyFormGroup>

      <TaskyFormGroup
        label="検索タグ"
        htmlFor="tags_text"
        helper="カンマ区切りで入力。例: 手書き, 自然, 現場感, 高級感"
      >
        <TaskyInput
          type="text"
          id="tags_text"
          value={formData.tags_text}
          onChange={(e) => handleInputChange('tags_text', e.target.value)}
          placeholder="手書き, 自然, 現場感"
          disabled={isLoading}
        />
      </TaskyFormGroup>

      <ImageUpload
        label="参照画像"
        type="reference"
        value={formData.reference_image_url}
        onChange={(path) => handleInputChange('reference_image_url', path)}
        disabled={isLoading}
        helpText="スタイルの参照画像（任意）"
      />

      <ImageUpload
        label="サムネイル"
        type="thumbnail"
        value={formData.thumbnail_url}
        onChange={(path) => handleInputChange('thumbnail_url', path)}
        disabled={isLoading}
        helpText="一覧表示用のサムネイル画像（任意）"
      />

      <TaskyFormGroup
        label="並び順"
        htmlFor="sort_order"
        error={errors.sort_order || undefined}
        helper="数値が小さいほど上位に表示されます"
      >
        <TaskyInput
          type="number"
          id="sort_order"
          hasError={Boolean(errors.sort_order)}
          value={formData.sort_order}
          onChange={(e) => handleInputChange('sort_order', parseInt(e.target.value) || 0)}
          min="0"
          disabled={isLoading}
        />
      </TaskyFormGroup>

      <div className="space-y-1.5">
        <TaskySwitch
          id="is_active"
          label="有効"
          checked={formData.is_active}
          onChange={(e) => handleInputChange('is_active', e.target.checked)}
          disabled={isLoading}
        />
        <p className="text-xs text-gray-500">
          無効にするとユーザーには表示されません
        </p>
      </div>

      <div className="flex justify-end gap-3 border-t border-warm-border pt-4">
        <TaskyButton
          type="button"
          variant="outline"
          size="sm"
          onClick={onCancel}
          disabled={isLoading}
        >
          キャンセル
        </TaskyButton>
        <TaskyButton
          type="submit"
          size="sm"
          isLoading={isLoading}
          disabled={isLoading || isLoadingTemplate || Boolean(templateError)}
        >
          {isLoading
            ? (imageStyle ? '更新中...' : '作成中...')
            : (imageStyle ? '更新' : '作成')}
        </TaskyButton>
      </div>
    </form>
  );
};

export default ImageStyleForm;
